import { useEffect, useRef } from 'react';

import { LEADS_DEFAULT_MAX_SIZE } from '@app/pages/Leads/model';

interface RunLeadsRegistrySearchParams {
  maxItems: number;
  isNextPage: boolean;
}

interface UseLeadsRegistrySearchEffectsParams {
  activeBranchId: number;
  acceptedSearchSignature: string | null;
  hasAcceptedResult: boolean;
  isStale: boolean;
  refreshRequestId: number;
  requestedMaxSize: number;
  searchSignature: string;
  resetResult(): void;
  runSearch(params: RunLeadsRegistrySearchParams): Promise<void>;
}

// Owns the only trigger of `SearchLeads` for the registry.
//
// The effect must fire ONLY when the effective filters (`searchSignature`),
// the requested page size (`requestedMaxSize`), the active branch, or an
// explicit registry refresh request changes.
//
// `runSearch`, `resetResult`, `hasAcceptedResult`, and `acceptedSearchSignature`
// are intentionally kept out of the effect dependency list:
// - `runSearch`/`resetResult` close over the latest accepted leads and change
//   identity after every accepted result and realtime patch; depending on them
//   directly turned a completed search into a new search request, producing a
//   network-paced `SearchLeads` loop (~500–1000 ms per iteration).
// - `hasAcceptedResult`/`acceptedSearchSignature` change on every completed
//   search; adding them to deps caused a spurious extra `SearchLeads` call
//   immediately after every response.
// All four are kept in refs so the effect body can still read their latest
// values without triggering a re-run.
export const useLeadsRegistrySearchEffects = ({
  acceptedSearchSignature,
  activeBranchId,
  hasAcceptedResult,
  isStale,
  refreshRequestId,
  requestedMaxSize,
  searchSignature,
  resetResult,
  runSearch,
}: UseLeadsRegistrySearchEffectsParams) => {
  const prevSearchSignatureRef = useRef<string | null>(null);
  const prevRequestedMaxSizeRef = useRef<number | null>(null);

  const runSearchRef = useRef(runSearch);
  const resetResultRef = useRef(resetResult);
  const hasAcceptedResultRef = useRef(hasAcceptedResult);
  const acceptedSearchSignatureRef = useRef(acceptedSearchSignature);
  const isStaleRef = useRef(isStale);

  // Keep refs current on every render so the effect always reads the latest
  // values without those values being effect dependencies.
  runSearchRef.current = runSearch;
  resetResultRef.current = resetResult;
  hasAcceptedResultRef.current = hasAcceptedResult;
  acceptedSearchSignatureRef.current = acceptedSearchSignature;
  isStaleRef.current = isStale;

  useEffect(() => {
    if (!activeBranchId) return;

    const prevSearchSignature = prevSearchSignatureRef.current;
    const prevRequestedMaxSize = prevRequestedMaxSizeRef.current;
    const isFirstRun = prevSearchSignature === null;
    const isFiltersChanged =
      !isFirstRun && prevSearchSignature !== searchSignature;
    const hasValidAcceptedResult =
      hasAcceptedResultRef.current &&
      acceptedSearchSignatureRef.current === searchSignature;

    prevSearchSignatureRef.current = searchSignature;
    prevRequestedMaxSizeRef.current = requestedMaxSize;

    // Lead-Card return: the accepted result already matches the current
    // filters — skip the first search on remount. Unless the result was flagged
    // stale (e.g. a task was created/completed from the Lead Card): then the
    // cached rows are out of date and must be refetched.
    if (isFirstRun && hasValidAcceptedResult && !isStaleRef.current) {
      return;
    }

    // A new filter restarts from the first page. When the window is already
    // deeper than the initial size, reset it first; the reset changes
    // `requestedMaxSize`, which re-runs this effect and performs the search.
    if (isFiltersChanged && requestedMaxSize !== LEADS_DEFAULT_MAX_SIZE) {
      resetResultRef.current();

      return;
    }

    // `hasValidAcceptedResult` is true at load-more time (a prior search
    // completed for these filters) and the size increased — load the next page.
    const isNextPage =
      !isFirstRun &&
      !isFiltersChanged &&
      prevRequestedMaxSize !== null &&
      requestedMaxSize > prevRequestedMaxSize &&
      hasValidAcceptedResult;

    void runSearchRef.current({ maxItems: requestedMaxSize, isNextPage });
  }, [activeBranchId, refreshRequestId, requestedMaxSize, searchSignature]);
};
